<template>
  <header
    class="flex justify-between p-5 w-full"
    :class="$dm('bg-white', 'dark:bg-slate-950')"
  >
    <div class="flex items-center">
      <button
        v-if="showBackButton && !isArticlesPage"
        id="back-button"
        class="mr-2 -ml-1 pointer-events-auto"
        @click="onBackButtonClick"
        @keyup.enter="onBackButtonClick"
      >
        <fluent-icon
          icon="chevron-left"
          size="24"
          :class="$dm('text-black-900', 'dark:text-slate-50')"
        />
      </button>
      <img
        v-if="avatarUrl && !isArticleSection"
        class="h-8 w-8 rounded-full mr-3"
        :src="avatarUrl"
        alt="avatar"
      />
      <div>
        <div
          class="font-medium text-base flex items-center"
          :class="$dm('text-black-900', 'dark:text-slate-50')"
        >
          <span
            v-dompurify-html="isArticleSection ? articleHeaderTitle : title"
            class="mr-1 inbox-name cursor-pointer"
            :style="showInQueueBadge ? 'max-width:170px' : 'max-width:230px'"
            :title="isArticleSection ? articleHeaderTitle : title"
          />
          <div
            v-if="!isArticleSection"
            :class="
              `h-2 w-2 rounded-full leading-4
              ${isOnline ? 'bg-green-500' : 'hidden'}`
            "
          />
        </div>
        <div
          class="text-xs mt-1"
          :class="$dm('text-black-700', 'dark:text-slate-400')"
        >
          {{
            isAssistantAttached && conversationStatus !== 'open'
              ? $t('REPLY_TIME.IN_A_FEW_SECONDS')
              : replyWaitMessage
          }}
        </div>
      </div>
    </div>
    <header-actions
      :is-in-message-screen="this.$route.name === 'messages'"
      :show-popout-button="showPopoutButton"
    />
  </header>
</template>

<script>
import { mapGetters } from 'vuex';

import availabilityMixin from 'widget/mixins/availability';
import nextAvailabilityTime from 'widget/mixins/nextAvailabilityTime';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import HeaderActions from './HeaderActions';
import routerMixin from 'widget/mixins/routerMixin';
import darkMixin from 'widget/mixins/darkModeMixin.js';
import configMixin from 'widget/mixins/configMixin';
import assistantMixin from 'widget/mixins/assistantMixin';

export default {
  name: 'ChatHeader',
  components: {
    FluentIcon,
    HeaderActions,
  },
  mixins: [
    nextAvailabilityTime,
    availabilityMixin,
    routerMixin,
    darkMixin,
    configMixin,
    assistantMixin,
  ],
  props: {
    avatarUrl: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    showPopoutButton: {
      type: Boolean,
      default: false,
    },
    availableAgents: {
      type: Array,
      default: () => {},
    },
    articleHeaderTitle: {
      type: String,
      default: '',
    },
    articleBackAction: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters({
      widgetColor: 'appConfig/getWidgetColor',
      conversationAttributes: 'conversationAttributes/getConversationParams',
    }),
    isOnline() {
      const { workingHoursEnabled } = this.channelConfig;
      const anyAgentOnline = this.availableAgents.length > 0;

      if (workingHoursEnabled) {
        return this.isInBetweenTheWorkingHours;
      }
      return anyAgentOnline;
    },
    showBackButton() {
      return this.hasShowConversationHistory && this.$route.name !== 'home';
    },
    isArticleSection() {
      return this.$route.name.includes('article');
    },
    isArticlePage() {
      return this.$route.name === 'article';
    },
    isArticlesPage() {
      return this.$route.name === 'articles';
    },
    conversationStatus() {
      return this.conversationAttributes?.status;
    },
    showInQueueBadge() {
      return (
        this.conversationAttributes.status &&
        this.conversationAttributes.in_queue
      );
    },
  },
  methods: {
    onBackButtonClick(e) {
      window.logger.logUserAction('info', 'Back button clicked');

      e.preventDefault();
      e.stopPropagation();
      if (this.isArticleSection) {
        this.articleBackAction();
      } else {
        this.$store.dispatch('conversationAttributes/set', {
          id: null,
          did: null,
          status: null,
          in_queue: undefined,
          customAttributes: null,
        });
        this.replaceRoute('home');
      }
    },
  },
};
</script>
<style scoped>
.inbox-name {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: inline-block;
  line-height: 1.5;
}
</style>
